import _ from 'lodash'
import { gql } from '~/tada/gql'
import { defineStore } from 'pinia'
import { OpenFeature } from '@openfeature/web-sdk'

export const useFestivalManager = defineStore('festivalManager', () => {
  const session = useSession()
  const openFeature = useOpenFeature()
  const { $posthog } = useNuxtApp()

  const fetched = ref(false)
  // Signed-out visitors have nothing to fetch, so they're loaded as soon as we know that.
  const loaded = computed(() => fetched.value || (session.ready && !session.loading && !session.user))

  const { result, refetch, onResult, loading } = useQuery(
    FestivalManagerCache,
    () => ({
      userId: session.user?.id!,
      userIdInt: session.user?.id!,
      ffid: openFeature.getNumber('current_film_festival'),
      ffidNum: openFeature.getNumber('current_film_festival'),
    }),
    () => ({
      enabled: !!session.user,
    }),
  )

  onResult(({ loading }) => {
    if (!loading) {
      fetched.value = true
      OpenFeature.setContext({
        ...OpenFeature.getContext(),
        filmmaker: teams.value.length > 0,
      })

      $posthog().setPersonProperties({
        [`${openFeature.getNumber('current_film_festival')}_team_ids`]:
          teams.value.map(team => team.id),
        [`${openFeature.getNumber('current_film_festival')}_studio_ids`]:
          studios.value?.map(studio => studio.id),
        [`${openFeature.getNumber('current_film_festival')}_filmmaker`]:
          teams.value.length > 0,
        [`${openFeature.getNumber('current_film_festival')}_film_challenge_submitted`]:
          teams.value.some(t => !!t.submitted_at),
      })
    }
  })

  const teamRoles = computed(() => result.value?.team_roles)
  const countries = computed(() => result.value?.countries)
  const genres = computed(() => result.value?.genres)
  const festivalGenres = computed(() => result.value?.festival_genres)
  const studios = computed(() => result.value?.studios)
  const teams = computed(
    () =>
      _.chain(studios.value)
        .flatMap(s => s.teams)
        .filter(t => !!t.team_number)
        .value() || [],
  )
  const assetTypes = computed(() => result.value?.assetTypes)

  return {
    refetch,
    loading,
    loaded,
    teamRoles,
    countries,
    genres,
    festivalGenres,
    studios,
    teams,
    assetTypes,
  }
})
const TeamRole = gql(`fragment TeamRole on team_roles @_unmask {
  id name description
}`)

const Country = gql(`fragment Country on countries @_unmask {
  id name code
}`)

const StudioInfoLite = gql(`fragment StudioInfoLite on studios @_unmask {
  id name owner_id teams(where: {film_festival_id: {_eq: $ffidNum}}) { id name team_number submitted_at }
}`)

const Genre = gql(`fragment Genre on genres @_unmask {
  id name description
}`)

const FestivalGenre = gql(`fragment FestivalGenre on festival_genres @_unmask {
  id name description
}`)

const AssetType = gql(`fragment AssetType on asset_types @_unmask {
  id title
}`)

const FestivalManagerCache = gql(
  `query FestivalManagerCache($userId: numeric!, $userIdInt: Int!, $ffid: Int!, $ffidNum: numeric!) {
  team_roles(where: { name: { _is_null: false }}, order_by: {name: asc}) {
    ...TeamRole
  }
  countries(where: { name: { _is_null: false }}, order_by: {name: asc}) {
    ...Country
  }
  genres(order_by: {name: asc}) {
    ...Genre
  }
  festival_genres(where: { film_festival_id: { _eq: $ffid } }, order_by: {name: asc}) {
    ...FestivalGenre
  }
  studios(where: {_or: [
    {owner_id: {_eq: $userIdInt}},
    {teams: {owner_id: {_eq: $userId}}},
    {teams: {memberships: {user_id: {_eq: $userId}}}}
  ]}) {
    ...StudioInfoLite
  }
  assetTypes: asset_types(where: {_or: [{hidden: {_is_null: true}},{hidden: {_neq: "Y"}}]} order_by: {title: asc}) {
    ...AssetType
  }
}
`,
  [TeamRole, Country, StudioInfoLite, Genre, FestivalGenre, AssetType],
)
